import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import EnrollmentService from "../../../../services/enrollment";
import WaitlistService from "../../../../services/waitlist_service";
import { useTenant } from "../../../../hooks/useTenant";
import ConfirmDialog from "../../../../components/Common/ConfirmDialog";

/**
 * useEnrollments
 * Hook para gerenciar matrículas de um membro no perfil.
 * Busca matrículas ativas e canceladas, e permite cancelar.
 * Também busca entradas da lista de espera e as integra à lista.
 */
const useEnrollments = (member) => {
    const { idTenant, user } = useTenant();
    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [canceling, setCanceling] = useState(null); // ID da matrícula sendo cancelada

    const fetchEnrollments = useCallback(async () => {
        if (!idTenant || !member?.id) return;

        setLoading(true);
        try {
            // Buscar matrículas normais
            const data = await EnrollmentService.listAllMemberEnrollments(idTenant, member.id);

            // Buscar lista de espera do membro
            const waitlistEntries = await WaitlistService.getMemberWaitlist(idTenant, member.id);

            // Mapear waitlist entries para o mesmo formato de linha
            const waitlistRows = waitlistEntries.map(entry => ({
                id: entry.id,
                _waitlistEntryId: entry.id, // Para identificar na ação de cancelar
                type: "waitlist",
                activityName: entry.activityName || "",
                weekdayLabel: entry.weekdayLabel || "",
                startTime: entry.startTime || "",
                endTime: entry.endTime || "",
                employeeName: entry.employeeName || "",
                idClass: entry.idClass,
                status: "waiting",
                position: entry.position || 0,
                createdAt: entry.requestedAt || entry.createdAt,
                startDate: null,
                sessionDate: null,
            }));
            
            const statusOrder = { active: 0, suspended: 1, canceled: 2 };
            const getOrder = (row) => {
                if (row.type === 'waitlist') return -1;
                return statusOrder[row.status] ?? 3;
            };

            // Ordenar: waitlist → ativas → suspensas → canceladas
            const sorted = [...waitlistRows, ...data].sort((a, b) => {
                const orderDiff = getOrder(a) - getOrder(b);
                if (orderDiff !== 0) return orderDiff;
                // Dentro do mesmo status, mais recente primeiro
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                return dateB - dateA;
            });

            setEnrollments(sorted);
        } catch (error) {
            console.error("Erro ao buscar matrículas:", error);
            toast.error("Erro ao carregar matrículas do aluno.");
        } finally {
            setLoading(false);
        }
    }, [idTenant, member?.id]);

    useEffect(() => {
        fetchEnrollments();
    }, [fetchEnrollments]);

    const handleCancelEnrollment = async (enrollment) => {
        // Se for um item de lista de espera, usar fluxo de remoção de waitlist
        if (enrollment.type === 'waitlist' || enrollment._waitlistEntryId) {
            const isConfirmed = await ConfirmDialog({
                title: 'Cancelar lista de espera?',
                text: `Tem certeza que deseja remover "${member.fullName}" da lista de espera desta turma?`,
                confirmButtonText: 'Sim, remover da lista',
                confirmButtonColor: '#ff3d60',
            });

            if (!isConfirmed) return;

            setCanceling(enrollment.id);
            try {
                await WaitlistService.removeFromWaitlist(
                    idTenant, user, enrollment._waitlistEntryId || enrollment.id,
                    enrollment.idClass, member.id
                );
                toast.success("Removido da lista de espera com sucesso.");
                await fetchEnrollments();
            } catch (error) {
                console.error("Erro ao remover da lista de espera:", error);
                toast.error(error.message || "Erro ao remover da lista de espera.");
            } finally {
                setCanceling(null);
            }
            return;
        }

        // Fluxo normal de cancelamento de matrícula
        const isConfirmed = await ConfirmDialog({
            title: `Cancelar matrícula?`,
            text: `Tem certeza que deseja cancelar a matrícula de "${member.fullName}" na turma? Esta ação irá remover o aluno das sessões futuras desta turma.`,
            confirmButtonText: 'Sim, cancelar matrícula!',
            confirmButtonColor: '#ff3d60',
        });

        if (!isConfirmed) return;

        setCanceling(enrollment.id);
        try {
            await EnrollmentService.cancelEnrollment(idTenant, user, enrollment);
            toast.success("Matrícula cancelada com sucesso.");
            // Recarregar a lista
            await fetchEnrollments();
        } catch (error) {
            console.error("Erro ao cancelar matrícula:", error);
            toast.error(error.message || "Erro ao cancelar matrícula.");
        } finally {
            setCanceling(null);
        }
    };

    return {
        enrollments,
        loading,
        canceling,
        handleCancelEnrollment,
        refresh: fetchEnrollments
    };
};

export default useEnrollments;
